import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Trash2, Volume2 } from "lucide-react";
import { toast } from "sonner";
import { supabase, API, authHeaders } from "../lib/supabase";
import { readbackEnabled, setReadbackEnabled } from "../lib/speech";
import { closeDb } from "../lib/db";

export const VoiceReadbackCard = () => {
  const [on, setOn] = useState(readbackEnabled());
  const toggle = () => { setReadbackEnabled(!on); setOn(!on); };
  return (
    <section className="card fade-up" data-testid="voice-readback-card">
      <p className="eyebrow flex items-center gap-2"><Volume2 size={14} /> Voice</p>
      <label className="flex items-center justify-between gap-4 mt-2 cursor-pointer">
        <div><p className="font-bold">Voice readback</p><p className="text-sm text-muted">Read search results and job cards aloud.</p></div>
        <input type="checkbox" role="switch" checked={on} onChange={toggle} className="toggle" data-testid="voice-readback-toggle" aria-checked={on} />
      </label>
    </section>
  );
};

const wipeLocal = async () => {
  try { await supabase.auth.signOut({ scope: "local" }); } catch { /* already gone */ }
  localStorage.clear(); sessionStorage.clear();
  await closeDb();
  if (window.indexedDB?.databases) { for (const d of await indexedDB.databases()) indexedDB.deleteDatabase(d.name); } else indexedDB.deleteDatabase("dropit");
  if (window.caches) for (const k of await caches.keys()) await caches.delete(k);
};

export const DeleteAccountCard = () => {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const confirm = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/account/delete`, { method: "POST", headers: { "Content-Type": "application/json", ...(await authHeaders()) }, body: JSON.stringify({ confirm: typed }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Deletion failed");
      await wipeLocal();
      navigate("/", { replace: true });
      setTimeout(() => toast.success("Your account and all data were deleted"), 50);
    } catch (e) { toast.error(e.message); setBusy(false); }
  };
  return (
    <section id="delete-account" className="card danger-zone fade-up" data-testid="delete-account-card">
      <p className="eyebrow text-[#FF7A7A]">Danger zone</p>
      <p className="font-bold mt-1">Delete my account</p>
      <p className="text-sm text-muted">Removes your profile, jobs, media, wallet and payment records. Contractor links stop working. This cannot be undone.</p>
      <button type="button" className="btn-secondary text-[#FF7A7A] mt-4 flex items-center justify-center gap-2" onClick={() => setOpen(true)} data-testid="delete-account-button"><Trash2 size={16} /> Delete my account</button>
      {open && createPortal(
        <div className="sheet-backdrop" onClick={() => !busy && setOpen(false)} data-testid="delete-account-backdrop">
          <div className="sheet" onClick={(e) => e.stopPropagation()} data-testid="delete-account-confirm">
            <p className="eyebrow text-[#FF7A7A]">Permanent</p>
            <h2 className="card-title">Delete everything?</h2>
            <p className="text-sm text-muted mt-2">Type <strong>DELETE</strong> to confirm. Unused credits are forfeited.</p>
            <p className="text-xs text-muted mt-2" data-testid="withdraw-consent-note">Withdrawing consent deletes your account and data as described in our Privacy Policy.</p>
            <input className="field mt-3" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="DELETE" autoCapitalize="characters" data-testid="delete-account-input" />
            <div className="flex gap-3 mt-4">
              <button type="button" className="btn-secondary" onClick={() => setOpen(false)} disabled={busy} data-testid="delete-account-cancel">Keep my account</button>
              <button type="button" className="btn-danger" onClick={confirm} disabled={busy || typed !== "DELETE"} data-testid="delete-account-confirm-button">{busy ? "Deleting…" : "Delete forever"}</button>
            </div>
          </div>
        </div>, document.body)}
    </section>
  );
};
